import { Box } from "@mui/material";
import React from "react";
import { useInView } from "react-intersection-observer";

import LegacyVenatusAd from "../ad";
import { LazyAd as LegacyLazyAd } from "../LazyAd";
import { SafeStickyAd as LegacySafeStickyAd } from "../SafeStickyAd";
import { isVenatusProvider } from "./adProvider";
import {
  AdSenseUnit,
  AdSenseUnitName,
  getAdSenseUnitMinHeight,
} from "./AdSenseUnit";

interface ProviderAdProps {
  placementName: string;
  alias?: string;
  adSenseUnit?: AdSenseUnitName;
}

export const Ad: React.FC<ProviderAdProps> = ({
  placementName,
  alias,
  adSenseUnit,
}) => {
  if (isVenatusProvider()) {
    return <LegacyVenatusAd placementName={placementName} alias={alias} />;
  }

  if (!adSenseUnit) return null;

  return <AdSenseUnit key={adSenseUnit} unitName={adSenseUnit} />;
};

interface ProviderSafeStickyAdProps extends ProviderAdProps {
  debounceMs?: number;
  placeholder?: React.ReactNode;
  minHeight?: string | number;
}

export const SafeStickyAd: React.FC<ProviderSafeStickyAdProps> = ({
  placementName,
  alias,
  adSenseUnit,
  debounceMs = 500,
  placeholder,
  minHeight,
}) => {
  if (isVenatusProvider()) {
    return (
      <LegacySafeStickyAd
        placementName={placementName}
        alias={alias}
        debounceMs={debounceMs}
        placeholder={placeholder}
        minHeight={minHeight}
      />
    );
  }

  if (!adSenseUnit) return null;

  const reservedHeight =
    minHeight === undefined ? getAdSenseUnitMinHeight(adSenseUnit) : minHeight;

  return (
    <div style={{ minHeight: reservedHeight, width: "100%" }}>
      <AdSenseUnit key={adSenseUnit} unitName={adSenseUnit} />
    </div>
  );
};

type ProviderLazyAdProps = ProviderAdProps;

export const LazyAd: React.FC<ProviderLazyAdProps> = ({
  placementName,
  alias,
  adSenseUnit,
}) => {
  const { ref, inView } = useInView({
    rootMargin: "200px 0px",
    triggerOnce: true,
  });

  if (isVenatusProvider()) {
    return <LegacyLazyAd placementName={placementName} alias={alias} />;
  }

  if (!adSenseUnit) return null;

  return (
    <Box
      ref={ref}
      sx={{
        minHeight: getAdSenseUnitMinHeight(adSenseUnit),
        width: "100%",
      }}
    >
      {inView ? <AdSenseUnit key={adSenseUnit} unitName={adSenseUnit} /> : null}
    </Box>
  );
};
